<template>

    <v-modal :title="title">
        <template #nav>
            <v-nav>
                <v-nav-item :active="tab === 'quick'" :label="$t('labels.quick_import')" @click.native="setTab('quick')" />
                <v-nav-item :active="tab === 'add'" :label="$t('labels.manual_entry')" @click.native="setTab('add')" />
                <v-nav-item :active="tab === 'csv'" :label="$t('labels.upload_csv')" @click.native="setTab('csv')" />
            </v-nav>
        </template>
        <template #body>
            <div v-if="tab === 'quick'">
                <v-modal-section :title="$t('titles.integrations')" icon="fas fa-plug">
                    <div class="d-grid gap-10">
                        <v-button icon="fab fa-google" icon-color="#DB4437" theme="white" btn-block @click.native="$router.push({ name: 'settings.integrations.show', params: { slug: 'google-my-business' } })">{{ $t('labels.import_from_google') }}</v-button>
                        <v-button icon="fab fa-facebook" icon-color="#1778F2" theme="white" btn-block @click.native="$router.push({ name: 'settings.integrations.show', params: { slug: 'facebook' } })">{{ $t('labels.import_from_facebook') }}</v-button>
                    </div>
                </v-modal-section>
                <v-modal-section :title="$t('titles.scrape_a_website')" icon="fas fa-globe" date="2025-03-28" collapsed>
                    <template name="body">
                        <v-alert :message="error" />
                        <v-form-group>
                            <div class="sources">
                                <div v-for="s in scrapeSources" :key="s.slug" :class="{ 'source': true, 'selected': scrapeParams.source === s.slug }" @click="setSource(s.slug)">
                                    <div class="source-body"><i :class="s.sprite" :style="{ color: s.color }" /></div>
                                    <div class="source-footer"><span>{{ s.name }}</span></div>
                                </div>
                            </div>
                        </v-form-group>
                        <v-form-group v-if="scrapeParams.source" :label="$t('labels.url')">
                            <v-input :placeholder="scrapePlaceholders[scrapeParams.source]" :model="scrapeParams.url" @input="scrapeParams.url = $event" />
                        </v-form-group>
                        <v-form-group v-if="scrapeParams.source && project.locations.length" :label="$t('labels.location')">
                            <v-select-location :model="scrapeParams.location_id" @input="scrapeParams.location_id = $event" />
                        </v-form-group>
                        <v-form-group v-if="scrapeParams.source && !['linkedin'].includes(scrapeParams.source)">
                            <v-checkbox :label="$t('labels.positive_only')" :model="scrapeParams.positive_only" @input="scrapeParams.positive_only = $event" />
                        </v-form-group>
                    </template>
                    <template v-if="scrapeParams.source" #footer>
                        <v-button size="sm" :loading="loading" @click.native="scrapeReviews">{{ $t('labels.import_recent_reviews') }}</v-button>
                    </template>
                </v-modal-section>
            </div>
            <div v-if="tab === 'add'">
                <v-alert :message="error" />
                <v-modal-section :title="$t('titles.customer')" icon="fas fa-user">
                    <v-form-group :label="$t('labels.first_name')" required>
                        <v-input :model="params.customer.first_name" @input="params.customer.first_name = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.last_name')">
                        <v-input :model="params.customer.last_name" @input="params.customer.last_name = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.email_address')">
                        <v-input type="email" :model="params.customer.email" @input="params.customer.email = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.phone_number')">
                        <v-input placeholder="+18888888888" :model="params.customer.phone" @input="params.customer.phone = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.company')">
                        <v-input :model="params.company" @input="params.company = $event" />
                    </v-form-group>
                </v-modal-section>
                <v-modal-section :title="$t('titles.review')" icon="fas fa-star">
                    <v-form-group :label="$t('labels.rating')" required>
                        <v-select-rating :model="params.score" @input="params.score = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.review')">
                        <v-textarea :rows="10" :model="params.review" @input="params.review = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.date')">
                        <v-date-picker :model="params.created_at" @input="params.created_at = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.source')">
                        <v-select-source :model="params.source_id" @input="params.source_id = $event" />
                    </v-form-group>
                    <v-form-group v-if="project.locations.length" :label="$t('labels.location')">
                        <v-select-location :model="params.location_id" @input="params.location_id = $event" />
                    </v-form-group>
                    <v-form-group :label="$t('labels.tags')">
                        <v-multiselect-tags context="review" :model="params.tag_ids" @input="params.tag_ids = $event" />
                    </v-form-group>
                </v-modal-section>
            </div>
            <div v-else-if="tab === 'csv'">
                <v-modal-section :title="$t('titles.select_csv_file')" icon="fas fa-file-csv">
                    <template #body>
                        <v-alert :message="error" />
                        <v-form-group :caption="$t('captions.csv_max_rows', { count: '1,000' })">
                            <v-uploader-file @input="csvParams.file = $event" />
                        </v-form-group>
                    </template>
                    <template #footer>
                        <v-button size="sm" theme="white" icon="fas fa-file" @click.native="downloadTemplate">{{ $t('labels.download_template') }}</v-button>
                        <v-button size="sm" :loading="loading" @click.native="createReviewsWithCSV">{{ $t('labels.upload_csv') }}</v-button>
                    </template>
                </v-modal-section>
                <v-modal-section :title="$t('titles.fields')" icon="fas fa-line-columns" collapsed>
                    <template #body>
                        <v-table bordered vlines>
                            <thead>
                                <th>{{ $t('labels.header') }}</th>
                                <th>{{ $t('labels.value') }}</th>
                            </thead>
                            <tbody>
                                <tr><td>first_name*</td><td>{{ $t('labels.import_first_name') }}</td></tr>
                                <tr><td>last_name</td><td>{{ $t('labels.import_last_name') }}</td></tr>
                                <tr><td>email</td><td>{{ $t('labels.import_email') }}</td></tr>
                                <tr><td>phone</td><td>{{ $t('labels.import_phone') }}</td></tr>
                                <tr><td>company</td><td>{{ $t('labels.import_company') }}</td></tr>
                                <tr><td>score*</td><td>{{ $t('labels.import_score') }}</td></tr>
                                <tr><td>review</td><td>{{ $t('labels.import_review') }}</td></tr>
                                <tr><td>source</td><td>{{ $t('labels.import_slug') }} <router-link :to="{ name: 'settings.sources' }">{{ $t('labels.source') }}</router-link>.</td></tr>
                                <tr><td>location</td><td>{{ $t('labels.import_slug') }} <router-link :to="{ name: 'settings.locations' }">{{ $t('labels.location') }}</router-link>.</td></tr>
                                <tr><td>tags</td><td>{{ $t('labels.import_list') }} <router-link :to="{ name: 'settings.tags' }">{{ $t('labels.tags') }}</router-link>.</td></tr>
                                <tr><td>created_at</td><td>{{ $t('labels.import_created_at') }} YYYY-MM-DD</td></tr>
                            </tbody>
                        </v-table>
                    </template>
                </v-modal-section>
            </div>
        </template>
        <template #footer>
            <v-button size="sm" theme="white" @click.native="cancel">{{ $t('labels.cancel') }}</v-button>
            <v-button v-if="tab === 'add'" size="sm" :loading="loading" @click.native="createReview">{{ $t('labels.save') }}</v-button>
            <div v-else></div>
        </template>
    </v-modal>

</template>

<style scoped lang="scss">

    .sources {
        display: grid;
        gap: 10px;
        grid-template-columns: repeat(3, 1fr);
        .source {
            background: $color-white;
            border: 1px solid $color-rule;
            border-radius: 5px;
            cursor: pointer;
            overflow: hidden;
            transition: .3s border-color;
            &:hover, &.selected {
                border-color: $color-text;
            }
            .source-body {
                align-items: center;
                color: $color-text;
                display: flex;
                font-size: 20px;
                height: 60px;
                justify-content: center;
            }
            .source-footer {
                @include font-medium(13px);
                background-color: $color-bg;
                color: $color-text;
                overflow: hidden;
                padding: 10px 0;
                text-align: center;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }
    }

</style>

<script>

import ProjectApi from '@api/ProjectApi';
import Modal from '@cmp/Modal';
import ModalSection from '@cmp/ModalSection';
import Nav from '@cmp/Nav';
import NavItem from '@cmp/NavItem';
import DatePicker from '@forms/DatePicker';
import MultiselectTags from '@forms/MultiselectTags';
import SelectLocation from '@forms/SelectLocation';
import SelectRating from '@forms/SelectRating';
import SelectSource from '@forms/SelectSource';
import UploaderFile from '@forms/UploaderFile';
import moment from 'moment';

export default {

    components: {
        'v-date-picker': DatePicker,
        'v-modal': Modal,
        'v-modal-section': ModalSection,
        'v-multiselect-tags': MultiselectTags,
        'v-nav': Nav,
        'v-nav-item': NavItem,
        'v-select-location': SelectLocation,
        'v-select-rating': SelectRating,
        'v-select-source': SelectSource,
        'v-uploader-file': UploaderFile,
    },

    data() {
        return {
            loading: false,
            error: null,
            tab: 'quick',
            // Scrape
            scrapeParams: {
                location_id: this.$store.getters.locationId,
                source: null,
                url: null,
            },
            scrapePlaceholders: {
                'google': 'https://www.google.com/maps/place/[ID]',
                'facebook': 'https://www.facebook.com/[ID]/reviews',
                'trustpilot': 'https://www.trustpilot.com/review/[ID]',
                'yelp': 'https://www.yelp.com/biz/[ID]',
                'linkedin': 'https://www.linkedin.com/in/[ID]',
            },
            scrapeSources: [
                {
                    name: 'Google',
                    slug: 'google',
                    color: '#DB4437',
                    sprite: 'fab fa-google',
                },
                {
                    name: 'Facebook',
                    slug: 'facebook',
                    color: '#1778F2',
                    sprite: 'fab fa-facebook',
                },
                {
                    name: 'Trustpilot',
                    slug: 'trustpilot',
                    color: '#05B57A',
                    sprite: 'fak fa-trustpilot',
                },
                {
                    name: 'Yelp',
                    slug: 'yelp',
                    color: '#C41200',
                    sprite: 'fab fa-yelp',
                },
                {
                    name: 'LinkedIn',
                    slug: 'linkedin',
                    color: '#0077B5',
                    sprite: 'fab fa-linkedin',
                },
            ],
            // CSV
            csvParams: {},
            // Manual
            params: {
                customer: {},
                score: 5,
                location_id: this.$store.getters.locationId,
                tag_ids: [],
                created_at: moment().unix(),
            },
        }
    },

    computed: {

        title() {
            return this.$t('labels.import_reviews');
        },

    },

    methods: {

        createReview() {
            this.loading = true;
            ProjectApi.createReview(this.project.id, this.params)
                .then((response) => {
                    this.loading = false;
                    this.$store.commit('setAlertSuccess', this.$t('messages.saved'));
                    this.$store.commit('setModal', null);
                    this.$emit('saved');
                })
                .catch((error) => {
                    this.loading = false;
                    this.error = error.response.data.message;
                    const modals = document.getElementsByClassName('modal');
                    [].forEach.call(modals, function(el) {
                        el.scrollTo(0, 0);
                    });
                });
        },

        createReviewsWithCSV() {
            this.loading = true;
            let csvParams = new FormData();
            csvParams.append('file', this.csvParams.file);
            ProjectApi.createReviewsWithCSV(this.project.id, csvParams)
                .then((response) => {
                    this.loading = false;
                    this.$store.commit('startOperation', {
                        type: 'review_import',
                        projectId: this.project.id,
                    });
                    this.$store.commit('setModal', null);
                    this.$emit('saved');
                })
                .catch((error) => {
                    this.loading = false;
                    this.error = error.response.data.message;
                });
        },

        scrapeReviews() {
            this.loading = true;
            ProjectApi.scrapeReviews(this.project.id, this.scrapeParams)
                .then((response) => {
                    this.loading = false;
                    this.$store.commit('startOperation', {
                        type: 'review_scrape',
                        projectId: this.project.id,
                    });
                    this.$store.commit('setModal', null);
                    this.$emit('saved');
                })
                .catch((error) => {
                    this.loading = false;
                    this.error = error.response.data.message;
                    const modals = document.getElementsByClassName('modal');
                    [].forEach.call(modals, function(el) {
                        el.scrollTo(0, 0);
                    });
                });
        },

        setTab(val) {
            this.tab = val;
            this.error = null;
        },

        setSource(val) {
            this.scrapeParams.url = null;
            if (this.scrapeParams.source === val) {
                this.scrapeParams.source = null;
            } else {
                this.scrapeParams.source = val;
            }
        },

        downloadTemplate() {
            const link = document.createElement('a');
            link.href = '/csv/reviews.csv';
            link.setAttribute('download', 'reviews.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        cancel() {
            this.$store.commit('setModal', null);
        },

    },

}

</script>
